import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchSubscriptions,
  fetchTransactions,
  downloadSubscriptions,
  downloadTransactions,
} from '../../api/adminClient';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters';
import StatusBadge from '../common/StatusBadge';
import DateRangePicker from '../common/DateRangePicker';

// ─── helpers ────────────────────────────────────────────────────────────────

const MIN_DATE = '2026-01-01';

const subscriptionUserLabel = (r) => r?.user?.emailAddress ?? r?.user?.uuid ?? '—';
const subscriptionPlanLabel = (r) => r?.plan?.plan_name ?? '—';
const transactionUserLabel = (t) => t?.user?.emailAddress ?? t?.user?.uuid ?? '—';
const transactionTypeLabel = (type) => (type === 'hours_pack' ? 'Add-on Hours' : (type ?? '—'));

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
];

const TXN_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
];

const pad = (n) => String(n).padStart(2, '0');

const toIsoDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const lastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const getDurationRange = (duration) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  if (duration === 'current') {
    return { from: `${y}-${pad(m + 1)}-01`, to: toIsoDate(now) };
  }
  if (duration === 'previous') {
    const prevY = m === 0 ? y - 1 : y;
    const prevM = m === 0 ? 11 : m - 1;
    const last = lastDayOfMonth(prevY, prevM);
    return { from: `${prevY}-${pad(prevM + 1)}-01`, to: `${prevY}-${pad(prevM + 1)}-${pad(last)}` };
  }
  if (duration === 'last3') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 3);
    d.setDate(1);
    if (d < new Date(MIN_DATE)) d.setTime(new Date(MIN_DATE).getTime());
    return { from: toIsoDate(d), to: toIsoDate(now) };
  }
  return null;
};

const normalizePagination = (pagination, count = 0) => {
  if (!pagination) return null;
  const toNum = (v, fb) => { const n = Number(v); return Number.isFinite(n) ? n : fb; };
  const total = Math.max(0, toNum(pagination.total ?? pagination.totalItems ?? pagination.count ?? count, count));
  const limit = Math.max(1, toNum(pagination.limit ?? pagination.perPage ?? 10, 10));
  const page = Math.max(1, toNum(pagination.page ?? pagination.currentPage ?? 1, 1));
  const computedPages = Math.max(1, Math.ceil(total / limit));
  const pages = Math.max(1, toNum(pagination.pages ?? pagination.totalPages ?? computedPages, computedPages));
  return { total, limit, page, pages };
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// ─── Paginator ───────────────────────────────────────────────────────────────

const Paginator = ({ pagination, onPageChange, loading }) => {
  if (!pagination || pagination.pages <= 1) return null;
  const { page, pages } = pagination;
  const items = Array.from({ length: pages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === pages || Math.abs(p - page) <= 2)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <small className="text-muted">
        Page {page} of {pages} &nbsp;·&nbsp; {pagination.total} total records
      </small>
      <nav>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => onPageChange(page - 1)} disabled={loading || page <= 1}>
              &laquo;
            </button>
          </li>
          {items.map((p, idx) =>
            p === '…' ? (
              <li key={`ellipsis-${idx}`} className="page-item disabled">
                <span className="page-link">…</span>
              </li>
            ) : (
              <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                <button className="page-link" onClick={() => onPageChange(p)} disabled={loading}>
                  {p}
                </button>
              </li>
            )
          )}
          <li className={`page-item ${page >= pages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => onPageChange(page + 1)} disabled={loading || page >= pages}>
              &raquo;
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

// ─── Download Ledger Modal ────────────────────────────────────────────────────

const DownloadModal = ({ activeTab, onClose }) => {
  const [duration, setDuration] = useState('current');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const validate = () => {
    if (duration !== 'custom') return null;
    if (!customFrom || !customTo) return 'Please select both From and To dates.';
    if (customFrom < MIN_DATE) return `From date cannot be before ${MIN_DATE}.`;
    if (customTo < MIN_DATE) return `To date cannot be before ${MIN_DATE}.`;
    if (customFrom > customTo) return 'From date must be before or equal to To date.';
    return null;
  };

  const handleDownload = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setError('');
    setDownloading(true);

    const range = duration === 'custom'
      ? { from: customFrom, to: customTo }
      : getDurationRange(duration);

    const today = new Date().toISOString().split('T')[0];
    const filename = activeTab === 'subscriptions'
      ? `subscriptions-${today}.csv`
      : `transactions-${today}.csv`;

    try {
      const fn = activeTab === 'subscriptions' ? downloadSubscriptions : downloadTransactions;
      const response = await fn({ ...range, export: 'csv' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      triggerBlobDownload(blob, filename);
      onClose();
    } catch (err) {
      setError('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const today = toIsoDate(new Date());

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4 shadow-lg border-0">
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title fw-semibold">Download Ledger</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={downloading} />
          </div>
          <div className="modal-body pt-3">
            <p className="text-muted small mb-3">
              Select a duration for the <strong>{activeTab}</strong> export.
            </p>

            <label className="form-label fw-semibold small text-uppercase text-muted">Duration</label>
            <div className="d-flex flex-column gap-2 mb-3">
              {[
                { value: 'current', label: 'Current Month' },
                { value: 'previous', label: 'Previous Month' },
                { value: 'last3', label: 'Last 3 Months' },
                { value: 'custom', label: 'Custom' },
              ].map((opt) => (
                <div key={opt.value} className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`dur-${opt.value}`}
                    name="duration"
                    value={opt.value}
                    checked={duration === opt.value}
                    onChange={() => { setDuration(opt.value); setError(''); }}
                  />
                  <label className="form-check-label" htmlFor={`dur-${opt.value}`}>
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>

            {duration === 'custom' && (
              <div className="row g-3 mb-2">
                <div className="col-6">
                  <label className="form-label small fw-semibold">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm rounded-3"
                    min={MIN_DATE}
                    max={today}
                    value={customFrom}
                    onChange={(e) => { setCustomFrom(e.target.value); setError(''); }}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small fw-semibold">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm rounded-3"
                    min={MIN_DATE}
                    max={today}
                    value={customTo}
                    onChange={(e) => { setCustomTo(e.target.value); setError(''); }}
                  />
                </div>
              </div>
            )}

            {error && <p className="text-danger small mb-0 mt-1">{error}</p>}
          </div>
          <div className="modal-footer border-0 pt-0">
            <button type="button" className="btn btn-outline-secondary rounded-pill" onClick={onClose} disabled={downloading}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary rounded-pill"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />Downloading…</>
              ) : (
                'Download'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PAGE_LIMIT = 10;

const SubscriptionsAndTransactions = () => {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [statusFilter, setStatusFilter] = useState('all');
  const [txnStatusFilter, setTxnStatusFilter] = useState('all');
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [subscriptions, setSubscriptions] = useState([]);
  const [subsPagination, setSubsPagination] = useState(null);
  const [subsPage, setSubsPage] = useState(1);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsSearch, setSubsSearch] = useState('');
  const [debouncedSubsSearch, setDebouncedSubsSearch] = useState('');
  const [subsDateFrom, setSubsDateFrom] = useState('');
  const [subsDateTo, setSubsDateTo] = useState('');

  const [transactions, setTransactions] = useState([]);
  const [txnPagination, setTxnPagination] = useState(null);
  const [txnPage, setTxnPage] = useState(1);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnSearch, setTxnSearch] = useState('');
  const [debouncedTxnSearch, setDebouncedTxnSearch] = useState('');
  const [txnDateFrom, setTxnDateFrom] = useState('');
  const [txnDateTo, setTxnDateTo] = useState('');

  const subsAbort = useRef(null);
  const txnAbort = useRef(null);
  const subsSearchDebounce = useRef(null);
  const txnSearchDebounce = useRef(null);

  useEffect(() => {
    clearTimeout(subsSearchDebounce.current);
    subsSearchDebounce.current = setTimeout(() => {
      setDebouncedSubsSearch(subsSearch);
      setSubsPage(1);
    }, 350);
    return () => clearTimeout(subsSearchDebounce.current);
  }, [subsSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(txnSearchDebounce.current);
    txnSearchDebounce.current = setTimeout(() => {
      setDebouncedTxnSearch(txnSearch);
      setTxnPage(1);
    }, 350);
    return () => clearTimeout(txnSearchDebounce.current);
  }, [txnSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubscriptions = useCallback(async (page) => {
    if (subsAbort.current) subsAbort.current.abort();
    subsAbort.current = new AbortController();
    setSubsLoading(true);
    try {
      const params = { limit: PAGE_LIMIT, page, status: statusFilter !== 'all' ? statusFilter : undefined };
      if (debouncedSubsSearch) params.emailAddress = debouncedSubsSearch;
      if (subsDateFrom) params.from = subsDateFrom;
      if (subsDateTo) params.to = subsDateTo;
      const res = await fetchSubscriptions(params);
      const list = res.data?.subscriptions || [];
      setSubscriptions(list);
      setSubsPagination(normalizePagination(res.data?.pagination, list.length));
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        setSubscriptions([]);
        setSubsPagination(null);
      }
    } finally {
      setSubsLoading(false);
    }
  }, [debouncedSubsSearch, subsDateFrom, subsDateTo, statusFilter]);

  const loadTransactions = useCallback(async (page) => {
    if (txnAbort.current) txnAbort.current.abort();
    txnAbort.current = new AbortController();
    setTxnLoading(true);
    try {
      const params = { limit: PAGE_LIMIT, page, status: txnStatusFilter !== 'all' ? txnStatusFilter : undefined };
      if (debouncedTxnSearch) params.emailAddress = debouncedTxnSearch;
      if (txnDateFrom) params.from = txnDateFrom;
      if (txnDateTo) params.to = txnDateTo;
      const res = await fetchTransactions(params);
      const list = res.data?.transactions || [];
      setTransactions(list);
      setTxnPagination(normalizePagination(res.data?.pagination, list.length));
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        setTransactions([]);
        setTxnPagination(null);
      }
    } finally {
      setTxnLoading(false);
    }
  }, [debouncedTxnSearch, txnDateFrom, txnDateTo, txnStatusFilter]);

  useEffect(() => { loadSubscriptions(subsPage); }, [loadSubscriptions, subsPage]);
  useEffect(() => { loadTransactions(txnPage); }, [loadTransactions, txnPage]);

  const handleSubsPageChange = (p) => setSubsPage(p);
  const handleTxnPageChange = (p) => setTxnPage(p);

  const currentFilters = activeTab === 'subscriptions' ? STATUS_FILTERS : TXN_STATUS_FILTERS;
  const currentFilter = activeTab === 'subscriptions' ? statusFilter : txnStatusFilter;
  const setCurrentFilter = activeTab === 'subscriptions' ? setStatusFilter : setTxnStatusFilter;
  const activeFilterLabel = currentFilters.find((o) => o.value === currentFilter)?.label || 'All';
  const loading = activeTab === 'subscriptions' ? subsLoading : txnLoading;

  return (
    <div className="subscriptions-view">
      {showDownloadModal && (
        <DownloadModal activeTab={activeTab} onClose={() => setShowDownloadModal(false)} />
      )}

      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
        <div>
          <h1 className="display-6 fw-semibold mb-1">Subscriptions & Transactions</h1>
          <p className="text-muted mb-0">
            Inspect renewals, billing status, and payment health for every SLP account.
          </p>
        </div>
        <div className="d-flex gap-2">
          <div className="dropdown">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill shadow-sm dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              disabled={loading}
            >
              Filter
              {currentFilter !== 'all' && (
                <span className="badge rounded-pill bg-primary ms-2">{activeFilterLabel}</span>
              )}
            </button>
            <ul className="dropdown-menu dropdown-menu-end shadow-sm">
              <li><h6 className="dropdown-header">Status</h6></li>
              {currentFilters.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    className={`dropdown-item ${currentFilter === opt.value ? 'active' : ''}`}
                    onClick={() => setCurrentFilter(opt.value)}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className="btn btn-primary rounded-pill shadow-sm"
            disabled={loading}
            onClick={() => setShowDownloadModal(true)}
          >
            Download Ledger
          </button>
        </div>
      </div>

      <div className="card-soft mb-4 p-3 d-flex flex-column flex-md-row gap-3">
        <input
          className="form-control rounded-pill shadow-sm flex-grow-1"
          placeholder="Search by user email…"
          value={activeTab === 'subscriptions' ? subsSearch : txnSearch}
          onChange={(e) => activeTab === 'subscriptions' ? setSubsSearch(e.target.value) : setTxnSearch(e.target.value)}
        />
        <DateRangePicker
          className="form-control rounded-pill shadow-sm"
          style={{ maxWidth: 220 }}
          from={activeTab === 'subscriptions' ? subsDateFrom : txnDateFrom}
          to={activeTab === 'subscriptions' ? subsDateTo : txnDateTo}
          onChange={(from, to) => {
            if (activeTab === 'subscriptions') {
              setSubsDateFrom(from);
              setSubsDateTo(to);
              setSubsPage(1);
            } else {
              setTxnDateFrom(from);
              setTxnDateTo(to);
              setTxnPage(1);
            }
          }}
        />
        {((activeTab === 'subscriptions' && (subsDateFrom || subsDateTo)) ||
          (activeTab === 'transactions' && (txnDateFrom || txnDateTo))) && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-pill"
            onClick={() => {
              if (activeTab === 'subscriptions') {
                setSubsDateFrom('');
                setSubsDateTo('');
                setSubsPage(1);
              } else {
                setTxnDateFrom('');
                setTxnDateTo('');
                setTxnPage(1);
              }
            }}
          >
            Clear dates
          </button>
        )}
      </div>

      <div className="card-soft">
        <ul className="nav nav-tabs border-0 gap-2 mb-0 px-1 pt-1" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'subscriptions'}
              className={`nav-link rounded-4 shadow-sm border-0 px-4 ${activeTab === 'subscriptions' ? 'active fw-semibold' : 'text-muted'}`}
              onClick={() => setActiveTab('subscriptions')}
            >
              Subscriptions
              <span className="badge rounded-pill ms-2 bg-success-subtle text-success">
                {subsPagination?.total ?? subscriptions.length}
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'transactions'}
              className={`nav-link rounded-4 shadow-sm border-0 px-4 ${activeTab === 'transactions' ? 'active fw-semibold' : 'text-muted'}`}
              onClick={() => setActiveTab('transactions')}
            >
              Transactions
              <span className="badge rounded-pill ms-2 bg-primary-subtle text-primary">
                {txnPagination?.total ?? transactions.length}
              </span>
            </button>
          </li>
        </ul>

        <div className="tab-content p-3 pt-4 border-top border-light mt-2">
          {activeTab === 'subscriptions' && (
            <div role="tabpanel" aria-label="Subscriptions">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-semibold mb-0">Active Subscriptions</h5>
                <span className="badge rounded-pill shadow-sm bg-success-subtle text-success">
                  {subsPagination ? `${subsPagination.total} total` : `${subscriptions.length} records`}
                  {statusFilter !== 'all' && ` · ${activeFilterLabel}`}
                </span>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="text-muted fw-semibold">User</th>
                      <th scope="col" className="text-muted fw-semibold">Plan</th>
                      <th scope="col" className="text-muted fw-semibold">Start</th>
                      <th scope="col" className="text-muted fw-semibold">End</th>
                      <th scope="col" className="text-end text-muted fw-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subsLoading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Loading…
                        </td>
                      </tr>
                    ) : subscriptions.length > 0 ? (
                      subscriptions.map((record) => (
                        <tr key={record.id ?? record.uuid} className="rounded-4 shadow-sm">
                          <td className="fw-semibold">{subscriptionUserLabel(record)}</td>
                          <td>
                            <div className="d-flex flex-column">
                              <span>{subscriptionPlanLabel(record)}</span>
                              <small className="text-muted">
                                {record?.plan?.plan_price != null && `${formatCurrency(record.plan.plan_price)} · `}
                                Auto-renew {record?.auto_renew ? 'enabled' : 'disabled'}
                              </small>
                            </div>
                          </td>
                          <td>{formatDate(record.start_date)}</td>
                          <td>{formatDate(record.end_date)}</td>
                          <td className="text-end">
                            <StatusBadge status={record.stripe_status || record.status} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">No subscriptions found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Paginator pagination={subsPagination} onPageChange={handleSubsPageChange} loading={subsLoading} />
            </div>
          )}

          {activeTab === 'transactions' && (
            <div role="tabpanel" aria-label="Transactions">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-semibold mb-0">Transaction History</h5>
                <span className="badge rounded-pill shadow-sm bg-primary-subtle text-primary">
                  Last updated {formatDateTime(new Date().toISOString())}
                </span>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="text-muted fw-semibold">Reference</th>
                      <th scope="col" className="text-muted fw-semibold">User</th>
                      <th scope="col" className="text-muted fw-semibold">Amount</th>
                      <th scope="col" className="text-muted fw-semibold">Type</th>
                      <th scope="col" className="text-muted fw-semibold" style={{ minWidth: 170 }}>Date</th>
                      <th scope="col" className="text-end text-muted fw-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txnLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Loading…
                        </td>
                      </tr>
                    ) : transactions.length > 0 ? (
                      transactions.map((txn) => (
                        <tr key={txn.id ?? txn.transaction_ref} className="rounded-4 shadow-sm">
                          <td className="fw-semibold">{txn.transaction_ref ?? '—'}</td>
                          <td>{transactionUserLabel(txn)}</td>
                          <td>{formatCurrency(txn.amount)}</td>
                          <td>
                            <span className="badge rounded-pill shadow-sm bg-light text-muted">
                              {transactionTypeLabel(txn.transaction_type)}
                            </span>
                          </td>
                          <td className="text-nowrap">{txn.createdAt ? formatDateTime(txn.createdAt) : '—'}</td>
                          <td className="text-end">
                            <StatusBadge status={txn.status} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">No transactions found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Paginator pagination={txnPagination} onPageChange={handleTxnPageChange} loading={txnLoading} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsAndTransactions;
